import { Component, inject, Input, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { OffcanvasService } from '@coreui/angular';
import { SettingsService, IBizEntity, IBizEntityType, BizEntityTypeCategory, BizEntityFunctionTypes, ItxnRelationshipLog } from '@optima-stride-ai/core';
import { BasePaginationController, ContextMenuClickInfo, ContextMenuItem, Explorer, IAddButtonOptions, IExplorerAttributes, IExplorerContentAttributes, IExplorerHeaderAttributes, IExplorerNavAttributes, IListView, IListViewColHeader, IListViewItem, IPaginationReqMessage, OffCanvasIDsInDefaultLayout, Page, getOffCanvasIdFromLevel, OffCanvasPlacement, openOffCanvas, OptimaOffCanvasService, ToastService } from "@optima-stride-ai/optima-components";
import { Subscription } from 'rxjs';
import { IBenBizEntity } from '../../../models/interfaces/biz-entity.model';
import { OffCanvasRightContentTypes, OffCanvasComponents, getOffCanvasComponentForBizEntityCategory } from '../../off-canvas/off-canvas-service/off-canvas-form.service';
import { AppStateService } from '../../state/app-state';
import { AppTagService, BizEntityChildrenStatusStoreService, EntityBottomOffCanvasService } from '@optima-stride-ai/optima-app-framework';
import { MeasureLogStateStoreService } from '../../transaction-log/measure-log-state-store/measure-log-state-store.service';
import { FormStates } from '../../utils/form/form-interfaces';
import { MeasuresStateStoreService } from '../measure-state-store/measures-state-store.service';
import { IMeasure } from '../measure.model';
import { BizEntityTypeStateStoreService } from '@optima-stride-ai/optima-app-framework';
import { BizEntityStateStoreService } from '@optima-stride-ai/optima-app-framework';
import { ContextMenuService } from '../../context-menu/context-menu';
import { LinkedEntityPickerService } from '../../linked-content-panel/linked-entity-picker/linked-entity-picker.service';
import { UtilService } from '../../utils/util.service';
import { ExplorerPaginationConfig } from '../../utils/explorer-pagination.config';
import { refreshExplorer, refreshExplorerNav } from '../../utils/explorer-nav-selection';
import { ExplorerReveal, ExplorerRoutes } from '../../utils/explorer-reveal';
import { getColHeadersForMeasure, getListViewForMeasures } from '../measure-list-view';
import { getColHeadersForBizEntity, getCombinedColHeadersForBizEntities, getListViewItemsForBizEntities, normalizeListItems } from '../../strategy/explorer-list-view';
import { filterBizEntitiesForApplicationExplorer, isExcludedFromApplicationExplorer } from '../../strategy/biz-entity-category-filters';
import { getColHeadersForTxnLog, getListViewForMeasureLogs } from '../../transaction-log/txn-log-list-view';
import { OffCanvasCollectiveComponent } from "../../off-canvas/off-canvas-form/off-canvas-collective/off-canvas-collective.component";
import { RelationshipStateStoreService } from '@optima-stride-ai/optima-app-framework';
import { it } from '@faker-js/faker';
@Component({
  selector: 'app-measure-explorer',
  imports: [Explorer],
  templateUrl: './measure-explorer.component.html',
  styleUrl: './measure-explorer.component.sass',
})
export class MeasureExplorerComponent extends BasePaginationController<IBizEntity> implements OnInit, OnDestroy {
@ViewChild('measureExplorer') measureExplorer!: Explorer;
  @Input('isInModal') isInModal: boolean = false;
  @Input('onSelectionChange') onSelectionChange?: (data: IBizEntity | undefined) => void;

  subHeaderAttributes: IExplorerHeaderAttributes = {
    search: true,
    filter: false,
    add: true,
    view: false,
    sideBar: true,
    addWithOptions: true,
    addButtonOptions: [],
    breadCrumbToggle: true,
    nextDisable: false,
    previouseDisable: false,
    appliedFilterCount: 0,
    graphView: false,
  };

  explorerNavAttributes: IExplorerNavAttributes = {
    data: [],
    isNavigable: false,
  };

  explorerContentAttributes: IExplorerContentAttributes = {
    data: [],
    listData: {
      title: '',
      colHeaders: [],
      items: [],
    },
    currentSelectedItem: '',
  };

  explorerAttributes: IExplorerAttributes = {
    data: [],
    subHeaderAttributes: this.subHeaderAttributes,
    explorerNavAttributes: this.explorerNavAttributes,
    explorerContentAttributes: this.explorerContentAttributes,
    isInModal: this.isInModal,
  };

  readonly appStateService: AppStateService = inject(AppStateService);
  readonly offCanvasFormService = inject(OptimaOffCanvasService);
  readonly offCanvasServiceRef = inject(OffcanvasService);
  readonly offcanvasServiceRef = inject(OffcanvasService);
  readonly entityBottomOffCanvasService = inject(EntityBottomOffCanvasService);
  readonly toastService = inject(ToastService);
  readonly settingsService = inject(SettingsService);
  // readonly visualizationTemplateService = inject(VisualizationTemplateService);
  readonly bizEntityTypeStateStoreService: BizEntityTypeStateStoreService = inject(BizEntityTypeStateStoreService);
  readonly measuresStateStoreService = inject(MeasuresStateStoreService);
  readonly measureLogStateStoreService = inject(MeasureLogStateStoreService);
  readonly contextMenuService = inject(ContextMenuService);
  private readonly linkedEntityPickerService = inject(LinkedEntityPickerService);
  readonly bizEntityStateStoreService = inject(BizEntityStateStoreService);
  readonly bizEntityChildrenStatusStoreService = inject(BizEntityChildrenStatusStoreService);
  readonly relationshipStateStoreService = inject(RelationshipStateStoreService);
  readonly appTagService = inject(AppTagService);
  private readonly utilService = inject(UtilService);

  measureData: IMeasure[] = [];
  strategyWithMeasureData: IBizEntity[] = [];
  measureLogData: ItxnRelationshipLog[] = [];
  bizEntityTypes: IBizEntityType[] = [];
  strategyData: IBizEntity[] = [];
  dataRefreshedToken: string = '';
  isLoading = signal<boolean>(true);
  measureStoreSubscription: Subscription | undefined;
  measureLogStoreSubscription: Subscription | undefined;
  bizEntityStoreSubscription: Subscription | undefined;
  relationshipStoreSubscription: Subscription | undefined;
  private deleteCompletedSubscription: Subscription | undefined;
  readonly contentItemCountPerPage = ExplorerPaginationConfig.ContentItemCountPerPage;
  private readonly measureLogTotalCount = new Map<string, number>();
  private contentRequestSequence = 0;
  private readonly explorerReveal = new ExplorerReveal({
    route: ExplorerRoutes.Measures,
    state: () => this.appStateService,
    explorer: () => this.measureExplorer,
    entities: () => this.explorerAttributes.data,
    fetch: (ids, ancestorsOf) => this.bizEntityStateStoreService.getState(
      this.bizEntityTypeStateStoreService, this.bizEntityChildrenStatusStoreService, ids, undefined, ancestorsOf),
    merge: entities => this.appendLoadedEntities(entities),
    openFolder: id => this.navItemClicked(id),
  });
  private readonly deletedEntityIds = new Set<string>();
  constructor() {
    super();
    this.contextMenuService.onMenuClicked = () => {
      setTimeout(() => {
        if (this.appStateService.offCanvasRightContentType === OffCanvasRightContentTypes.BizEntityForms) {
          this.openBizEntityFormInOffCanvas()
        } else if(this.appStateService.offCanvasRightContentType === OffCanvasRightContentTypes.DetailForm)
        {
          this.openDetailOffCanvas();
        }
        else {
          this.openOthersInRightOffCanvas()
        }
      })
    }
    this.contextMenuService.onPinToQuickActionClicked = () =>{
      setTimeout(() => {
        this.addToQuickActionClicked();
      })
    }
  }

  ngOnInit(): void {
    this.subscribeToStates();
    if (!this.isInModal) {
      this.explorerReveal.watch();
    }
    this.fetchData(false);
  }

  ngOnDestroy(): void {
    this.unSubscribeFromStates();
    this.deletedEntityIds.clear();
  }

  private unSubscribeFromStates() {
    if (this.measureStoreSubscription) {
      this.measureStoreSubscription.unsubscribe();
    }
    if (this.measureLogStoreSubscription) {
      this.measureLogStoreSubscription.unsubscribe();
    }
    if (this.bizEntityStoreSubscription) {
      this.bizEntityStoreSubscription.unsubscribe();
    }
    if(this.relationshipStoreSubscription){
      this.relationshipStoreSubscription.unsubscribe();
    }
    this.deleteCompletedSubscription?.unsubscribe();
  }

  private subscribeToStates() {
    if (this.measuresStateStoreService.changed) {
      this.measureStoreSubscription = this.measuresStateStoreService.changed.subscribe(() => {
        this.fetchData(true);
      });
    }
    if (this.measureLogStateStoreService.changed) {
      this.measureLogStoreSubscription = this.measureLogStateStoreService.changed.subscribe(() => {
        // Preserve selected measure by updating logs without full refresh
        this.updateMeasureLogsWithoutNavigation();
      });
    }
    if(this.bizEntityStateStoreService.changed){
      this.bizEntityStoreSubscription = this.bizEntityStateStoreService.changed.subscribe(() => {
        this.fetchData(true);
        
        // Force rebuild of content list with fresh tags
        // Thi check is there because when the component is 
        // initialized, the currentSelectedItem is empty ad we don't want
        //  to call updateListData at that time as it is not necessary
        const currentSelectedItem = this.explorerContentAttributes.currentSelectedItem;
        if (currentSelectedItem) {
          this.updateListData(currentSelectedItem);
        }
      });
    }
    if(this.relationshipStateStoreService.changed){
      this.relationshipStoreSubscription = this.relationshipStateStoreService.changed.subscribe(() => {
        if (this.appTagService.currentVersion() > this.appTagService.savedVersion()) {
          return;
        }
        // Fetch data with state change flag to reload all measure and log data
        // since relationships can link measures to any other entity
        this.fetchData(true);
      });
    }
    if (this.contextMenuService.logsEvent) {
      this.contextMenuService.logsEvent.subscribe((entity: IBizEntity) => {
        this.itemClicked(entity.id)
      });
    }
    this.deleteCompletedSubscription = this.contextMenuService.deleteCompletedEvent?.subscribe((entity) => {
      this.removeDeletedEntityFromExplorer(entity);
    });
  }

  private fetchData(isStateStoreServiceStateChanged:boolean) {
    this.isLoading.set(true);
    // this.measureLogMockDataService.measureData$.subscribe((data) => {
    //   this.measureData = data;
    // });
    // this.measureLogMockDataService.measureLogData$.subscribe((data) => {
    //   this.measureLogData = data;
    // });
    // this.bizEntityTypes = this.bizEntityTypeMockDataService.getCompanyDataBizEntityType();
    // const tenantId = getTenantIdInString(this.tenantService.organization?.tenantId);
    // this.measureData = this.measureLogMockDataService.getMeasures(tenantId, this.mockAPIResponseService);
    // this.measureLogData = this.measureLogMockDataService.getMeasureLogs(tenantId, this.mockAPIResponseService);
    // this.strategyData = this.benBizEntityMockDataService.getItems(tenantId, this.mockAPIResponseService);
    // this.setupExplorerOnInit();
    this.bizEntityTypeStateStoreService.getState().then((data) => {
      if (data) {
        this.bizEntityTypes = data;
      }

      this.defineSubHeaderAttributes();
      return this.fetchMeasureEntityPage(
        undefined,
        this.contentItemCountPerPage,
        0,
        [BizEntityTypeCategory.BenzaitenMeasureStore]
      );
    }).then(async (page) => {
      this.paginationBuffer.setInitialPage('', page);
      const ancestors = await this.fetchAncestorPath(page.items);
      this.strategyData = this.mergeEntities(this.strategyData, ancestors, page.items);
      this.setupExplorerOnInit(isStateStoreServiceStateChanged);
    }).catch((error) => {
      console.error('Error fetching measure explorer data:', error);
      this.isLoading.set(false);
    });

  }
  private contextMenuItems: ContextMenuItem[] = [];
  
  private loadMeasureLogsForMeasure(measureId: string, limit = this.contentItemCountPerPage, offset = 0): Promise<ItxnRelationshipLog[]> {
    return this.measureLogStateStoreService
      .getState(this.measuresStateStoreService, measureId, limit, offset)
      .then((data) => {
        const logs = data || [];
        const totalRecords = (logs[0] as (ItxnRelationshipLog & { totalRecords?: unknown }) | undefined)?.totalRecords;
        this.measureLogTotalCount.set(
          measureId,
          typeof totalRecords === 'number' ? totalRecords : offset + logs.length
        );
        return logs;
      })
      .catch((error) => {
        console.error(`Error fetching measure logs for measure ${measureId}:`, error);
        return [];
      });
  }
  
  getContextMenuItem () {
    this.contextMenuItems = this.contextMenuService.getMenu();
    this.explorerAttributes.contextMenuItems = this.contextMenuItems;
  }

  setupExplorerOnInit(isStateStoreServiceStateChanged:boolean) {
    this.mergeStrategyAndMeasureData();
    this.defineExplorerAttributes(isStateStoreServiceStateChanged);
    this.getContextMenuItem();
    this.isLoading.set(false);
    // this.applyFilters();

    // this.measureLogData = 
  }

  private mergeStrategyAndMeasureData() {
    // const tenantId = getTenantIdInString(this.tenantService.organization?.tenantId);
    // if (!tenantId) {
    //   // console.error('Tenant ID is invalid.');
    //   return;
    // }
    this.populateMeasureLogWithMeasureData();
    // this.strategyWithMeasureData = this.measureData;
    // this.strategyWithMeasureData = this.teamMockDataService.findAncestors(this.strategyWithMeasureData, this.strategyData);
    const tenantBizEntity = this.strategyData.find(item => item.type.category === BizEntityTypeCategory.Tenant);
    let measureStore = this.strategyData.filter((item) => item.type.category === BizEntityTypeCategory.BenzaitenMeasureStore);
    let measureStoreEntireHierarchy = this.utilService.findEntireHierarchy(measureStore, this.strategyData );
    this.strategyWithMeasureData = filterBizEntitiesForApplicationExplorer([ ...measureStoreEntireHierarchy]);
    if(this.strategyWithMeasureData.length === 0){
      this.strategyWithMeasureData = tenantBizEntity ? [tenantBizEntity] : [];
    }
  }

  populateMeasureLogWithMeasureData() {
    this.measureLogData.forEach((item) => {
      let measure = this.measureData.find((m) => m.id === item.bizEntity.id);
      if (measure) {
        item.bizEntity = measure;
      }
    });
    // console.log('measureLogData',this.measureLogData);
    // let parentId = ['8f64fbb4-e790-4ae7-9648-4742668347632868926','8f64fbb4-e790-4ae7-9648-47426637623288347632868926','8f64fbb4-e790-4ae7-9648-474266834763872138962868926','8f64fbb4-e790-4ae7-9648-4742jsd3829766834763872138962868926']
    // this.measureLogData.forEach((item) => {
    //   item.measure.logFieldsMeta.forEach((field:any) => {
    //     let key = field.name;
    //     let value;
    //     if(field.type === FieldTypes.number)
    //     {
    //       value = faker.number.int({max:1000});
    //     }else if(field.type === FieldTypes.text){
    //       value = faker.lorem.word();
    //     }else if(field.type === FieldTypes.date){
    //       value = faker.date.recent();
    //     }
    //     item.logFieldValues.push({key: key, value: value, fieldMeta: field});
    //   });
    //   item.measure = {id: item.measure.id};
    // });

    // console.log(this.measureLogData);
  }
  // subHeaderOptions:any[] = [];
  // private async defineSubHeaderAttributes(): Promise<void> {
  //   await this.prepareAddButtonOptions();

  //   if(this.isInModal) {
  //     this.subHeaderAttributes.add = false;
  //     this.subHeaderAttributes.addWithOptions = false;
  //     this.subHeaderAttributes.filter = false;
  //     this.subHeaderAttributes.graphView = false;
  //   }

  //   this.subHeaderAttributes.addButtonOptions = this.subHeaderOptions;
  // }

  // //move this service up
  // userService = inject(UserService);

  private subHeaderOptions: IAddButtonOptions[] = [];
  private prepareAddButtonOptions() {
    let options: IAddButtonOptions[] = [];
    // let optionBizEntityTypes = this.appStateService.getBizEntityTypes(this.tenantService.organization?.tenantId).filter( item => item);
    // Promise resolution pattern needs refactoring for better error handling and async flow
    // Initial implementation for state store service from hackathon sprint
    let data = this.bizEntityTypes
    data = this.bizEntityTypes.filter((item) => item.category === BizEntityTypeCategory.BenzaitenMeasure)
    data.forEach((item) => {
      options.push({ id: item.id, label: item.name, icon: item.icon });
    });

    this.subHeaderOptions = options;
  }


  onSubHeaderAddButtonClicked() {
    this.appStateService.selectedBizEntityForEdit = null;
    const entityType = this.bizEntityTypes.find((item) => item.category === BizEntityTypeCategory.BenzaitenMeasure && item.isSystemType);
    if (entityType) {
      this.appStateService.selectedBizEntityTypeForNewBizEntity = entityType;
    }
    this.appStateService.entityFormState = FormStates.New;
    const level = this.offCanvasFormService.currentOffCanvas?.level ?? 0;
    openOffCanvas(this.offCanvasFormService, this.offCanvasServiceRef, OffCanvasComponents.Measure, getOffCanvasIdFromLevel(level + 1), level + 1);
    setTimeout(() => {
      this.appStateService.entityFormState = FormStates.Idle;
    });
  }

  onAddButtonOptionClick = (option: IAddButtonOptions): void => {
    this.appStateService.selectedBizEntityForEdit = null;
    if (this.appStateService.entityFormState !== FormStates.Idle) {
      console.error("Entity form state is not idle. Cannot proceed with new entity creation.");
      return;
    }
    // Promise resolution pattern needs refactoring for better error handling and async flow
    // Initial implementation for state store service from hackathon sprint
    // const entityType = this.appStateService.getBizEntityTypeById(option.id);
    const entityType = this.bizEntityTypes.find(item => item.id === option.id);
    if (!entityType) {
      console.error("Invalid entity type.");
      return;
    }

    this.appStateService.selectedBizEntityTypeForNewBizEntity = entityType;

    const offCanvasComponent = getOffCanvasComponentForBizEntityCategory(entityType);
    if (offCanvasComponent !== null) {
      this.appStateService.entityFormState = FormStates.New;
      this.openBizEntityFormInOffCanvas();
    } else {
      console.error("No off-canvas component found for the selected biz entity type.");
    }
  }

  clickFilterButton = () => {
    // this.appStateService.activeView = Views.teams;
    // if (this.appStateService.getViewFilterCopy(Views.teams).length === 0) {
    //   // this.appStateService.viewFilter = this.filtersMockService.getFilterMocksFor(Views.teams, this.teamsMockDataService.getTableHeadingForUser(), this.strategyWithTeamData);
    // }
    // openOffCanvas(this.offCanvasFormService, this.offCanvasServiceRef, OffCanvasComponents.ViewFilter, OffCanvasIDsInDefaultLayout.l1_id, 1);
  }

  private defineExplorerAttributes(isStateStoreServiceStateChanged = false, data: IBizEntity[] = this.strategyWithMeasureData) {
    const revealId = isStateStoreServiceStateChanged || this.isInModal ? undefined : this.explorerReveal.takeInitialRequest();
    this.explorerAttributes.data = data;
    this.explorerAttributes.isInModal = this.isInModal;
    this.explorerAttributes.subHeaderAttributes = this.subHeaderAttributes;
    this.explorerAttributes.explorerNavAttributes = this.explorerNavAttributes;
    this.explorerAttributes.explorerContentAttributes = this.explorerContentAttributes;
    
    this.defineSubHeaderAttributes();
    this.defineExplorerNavAttributes();
    
    // Determine initial selection: incoming selection or root element
    const preSelectedItemId = this.appStateService.incomingBizEntitySelectionId;
    const rootElement = this.explorerAttributes.data.find(item => !item.parentId || item.parentId === '');
    const targetItemId = (preSelectedItemId && this.explorerAttributes.data.length > 0) 
      ? preSelectedItemId 
      : (rootElement?.id || this.explorerAttributes.data[0]?.id || '');
    
    this.defineExplorerContentAttributes(targetItemId);
    refreshExplorer(this);
    
    // Load data for the selected item
    if (isStateStoreServiceStateChanged) {
      let currentSelection = this.explorerContentAttributes.currentSelectedItem || 
                               this.explorerNavAttributes.currentSelectedItem;
      let refreshTargetId = currentSelection;
      if(this.appStateService.incomingBizEntitySelectionId && this.appStateService.incomingBizEntitySelectionId !== currentSelection){
        let newEntityId = this.appStateService.incomingBizEntitySelectionId;
        let element = this.explorerAttributes.data.find(item => item.id.toString() === newEntityId.toString());
        if(element){
          refreshTargetId = element.parentId || currentSelection;
        }
      }
      // Always refresh so newly created/edited entities under the current selection appear,
      // even when the incoming-selection entity itself isn't found yet.
      if (refreshTargetId) {
        this.updateListData(refreshTargetId);
      }
    } else if (targetItemId) {
      this.updateListData(targetItemId);
    }

    // Set preselected item for UI
    if (targetItemId) {
      this.explorerAttributes.preSelectedItemId = targetItemId;
    }
    if (revealId) {
      void this.explorerReveal.reveal(revealId);
    }
  }


  private defineSubHeaderAttributes(){
    this.prepareAddButtonOptions();
    if (this.isInModal) {
      this.subHeaderAttributes.add = false;
      this.subHeaderAttributes.addWithOptions = false;
      this.subHeaderAttributes.filter = false;
      this.subHeaderAttributes.graphView = false;
    }
    this.subHeaderAttributes.addButtonOptions = this.subHeaderOptions;
  }


  private defineExplorerNavAttributes() {
    this.explorerNavAttributes.data = [...this.explorerAttributes.data];
    if (!this.explorerNavAttributes.data.some(item => item.parentId === '')) {
      this.explorerNavAttributes.data = this.utilService.findAncestors(this.explorerNavAttributes.data, this.strategyData);
    }
    // this.explorerNavAttributes.userSettings = this.settingsService.settings.all?.user.benzaiten.explorers.teams;
  }

  updateListData = (itemId: string): Promise<void> => {
    const requestSequence = ++this.contentRequestSequence;
    this.explorerNavAttributes.currentSelectedItem = itemId;
    this.explorerContentAttributes.currentSelectedItem = itemId;
    const item = this.explorerAttributes.data.find((entity) => entity.id === itemId);
    const loadData = item?.type.category === BizEntityTypeCategory.BenzaitenMeasure
      ? this.loadMeasurePage(itemId, requestSequence)
      : this.loadEntityPage(itemId, requestSequence);

    return loadData
      .then((items) => {
        if (requestSequence !== this.contentRequestSequence) return;
        if (items) this.explorerContentAttributes.listData.items = items;
        this.setFloatingButtonAction(this.explorerContentAttributes.listData);
        this.defineExplorerContentAttributes(itemId, items);
      })
      .catch((error) => {
        console.error('Error updating list data:', error);
      });
  }

  /**
   * Update measure logs without changing navigation state.
   * Used during measure log data refreshes to preserve selected measure.
   * Avoids full explorer rebuild which would reset selection to root.
   */
  private updateMeasureLogsWithoutNavigation(): void {
    const currentSelectedId = this.explorerContentAttributes.currentSelectedItem;
    if (currentSelectedId) void this.updateListData(currentSelectedId);
  }

  private async loadEntityPage(itemId: string, requestSequence: number): Promise<IListViewItem<IBizEntity>[]> {
    const page = await this.fetchPaginationPage(itemId, this.contentItemCountPerPage, 0);
    await this.explorerReveal.extendPage(itemId, page,
      offset => this.fetchPaginationPage(itemId, this.contentItemCountPerPage, offset),
      () => requestSequence === this.contentRequestSequence);
    this.paginationBuffer.setInitialPage(itemId, page);
    this.appendLoadedEntities(page.items);
    const measureIds = page.items
      .filter((entity) => entity.type.category === BizEntityTypeCategory.BenzaitenMeasure)
      .map((entity) => entity.id);
    await this.ensureMeasuresLoaded(measureIds);
    return this.prepareListData(itemId);
  }

  private async loadMeasurePage(itemId: string, requestSequence: number): Promise<IListViewItem<IBizEntity>[]> {
    await this.ensureMeasuresLoaded([itemId]);
    const logs = await this.loadMeasureLogsForMeasure(itemId);
    if (requestSequence !== this.contentRequestSequence) return [];
    this.measureLogData = logs;
    return this.prepareListDataAsync(itemId);
  }

  private async ensureMeasuresLoaded(ids: string[]): Promise<void> {
    const loadedIds = new Set(this.measureData.map((measure) => measure.id.toString()));
    const idsToLoad = ids.filter((id) => !loadedIds.has(id.toString()));
    if (idsToLoad.length === 0) return;
    const data = await this.measuresStateStoreService.getState(this.bizEntityTypeStateStoreService, idsToLoad);
    const measuresById = new Map(this.measureData.map((measure) => [measure.id, measure]));
    (data ?? []).forEach((measure) => measuresById.set(measure.id, measure));
    this.measureData = Array.from(measuresById.values());
  }

  getContentTotalCount(): number {
    const itemId = this.explorerContentAttributes.currentSelectedItem || '';
    const item = this.explorerAttributes.data.find((entity) => entity.id === itemId);
    return item?.type.category === BizEntityTypeCategory.BenzaitenMeasure
      ? this.measureLogTotalCount.get(itemId) ?? this.measureLogData.length
      : this.getPaginationTotalCount(itemId, this.explorerContentAttributes.data.length);
  }

  override async onPaginationRequest(request: IPaginationReqMessage): Promise<void> {
    const isNavRequest = request.requesterId.startsWith('explorer-nav-');
    const itemId = this.explorerContentAttributes.currentSelectedItem || '';
    const item = this.explorerAttributes.data.find((entity) => entity.id === itemId);
    if (!isNavRequest && item?.type.category === BizEntityTypeCategory.BenzaitenMeasure) {
      if (this.measureLogData.length >= this.getContentTotalCount()) return;
      // Content-only pagination: keep the nav tree's loading state untouched so scrolling
      // the list doesn't blank the hierarchy with a full-tree skeleton.
      this.explorerNavAttributes.isLoading = false;
      this.setPaginationLoading(true);
      try {
        const logs = await this.loadMeasureLogsForMeasure(itemId, request.itemCountPerPage, this.measureLogData.length);
        if (itemId !== this.explorerContentAttributes.currentSelectedItem) return;
        const logsById = new Map(this.measureLogData.map((log) => [log.id, log]));
        logs.forEach((log) => logsById.set(log.id, log));
        this.measureLogData = Array.from(logsById.values());
        const items = await this.prepareListDataAsync(itemId);
        this.defineExplorerContentAttributes(itemId, items);
      } finally {
        this.setPaginationLoading(false);
      }
      return;
    }
    this.explorerNavAttributes.isLoading = isNavRequest;
    try {
      await super.onPaginationRequest(request);
    } finally {
      this.explorerNavAttributes.isLoading = false;
    }
  }

  protected override resolvePaginationParentId(request: IPaginationReqMessage): string {
    return request.parentId || this.explorerContentAttributes.currentSelectedItem || '';
  }

  protected override setPaginationLoading(isLoading: boolean): void {
    this.isLoading.set(isLoading);
  }

  protected override async fetchPaginationPage(parentId: string | undefined, limit: number, offset: number): Promise<Page<IBizEntity>> {
    if (parentId && !this.isInMeasureStoreSubtree(parentId)) {
      const pathChildren = this.strategyData.filter((entity) =>
        entity.parentId === parentId && !this.deletedEntityIds.has(entity.id.toString())
      );
      return {
        items: pathChildren.slice(offset, offset + limit),
        totalCount: pathChildren.length,
      };
    }
    return this.fetchMeasureEntityPage(parentId, limit, offset);
  }

  private async fetchMeasureEntityPage(
    parentId: string | undefined,
    limit: number,
    offset: number,
    categoryTypes: BizEntityTypeCategory[] = this.getExplorerCategories()
  ): Promise<Page<IBizEntity>> {
    const data = await this.bizEntityStateStoreService.getState(
      this.bizEntityTypeStateStoreService,
      this.bizEntityChildrenStatusStoreService,
      undefined,
      parentId,
      undefined,
      undefined,
      categoryTypes,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      limit,
      offset
    );
    const items = filterBizEntitiesForApplicationExplorer(data ?? [])
      .filter(entity => !this.deletedEntityIds.has(entity.id.toString()));
    const totalRecords = (data?.[0] as (IBizEntity & { totalRecords?: unknown }) | undefined)?.totalRecords;
    return { items, totalCount: typeof totalRecords === 'number' ? totalRecords : items.length };
  }

  protected override onNavPaginationChildrenLoaded(_parentId: string, appendedItems: IBizEntity[], _knownEmptyFolderIds: string[]): void {
    this.appendLoadedEntities(appendedItems);
  }

  protected override async onContentPaginationChildrenLoaded(parentId: string, appendedItems: IBizEntity[]): Promise<void> {
    if (parentId !== this.explorerContentAttributes.currentSelectedItem) return;
    this.appendLoadedEntities(appendedItems);
    const measureIds = appendedItems
      .filter((entity) => entity.type.category === BizEntityTypeCategory.BenzaitenMeasure)
      .map((entity) => entity.id);
    await this.ensureMeasuresLoaded(measureIds);
    this.defineExplorerContentAttributes(parentId);
  }

  private appendLoadedEntities(entities: IBizEntity[]): void {
    entities = entities.filter(entity => !this.deletedEntityIds.has(entity.id.toString()));
    if (entities.length === 0) return;
    this.strategyData = this.mergeEntities(this.strategyData, entities);
    this.mergeStrategyAndMeasureData();
    this.explorerAttributes.data = this.strategyWithMeasureData;
    this.explorerNavAttributes.data = [...this.strategyWithMeasureData];
    refreshExplorerNav(this.measureExplorer);
  }

  private async fetchAncestorPath(entities: IBizEntity[]): Promise<IBizEntity[]> {
    const ancestorPages = await Promise.all(entities.map((entity) =>
      this.bizEntityStateStoreService.getState(
        this.bizEntityTypeStateStoreService,
        this.bizEntityChildrenStatusStoreService,
        undefined,
        undefined,
        entity.id
      )
    ));
    return ancestorPages.flatMap((page) => page ?? []);
  }

  private mergeEntities(...groups: IBizEntity[][]): IBizEntity[] {
    const entitiesById = new Map<string, IBizEntity>();
    groups.flat().filter(entity => !this.deletedEntityIds.has(entity.id.toString()))
      .forEach((entity) => entitiesById.set(entity.id, entity));
    return Array.from(entitiesById.values());
  }

  private removeDeletedEntityFromExplorer(entity: IBizEntity): void {
    const deletedId = entity.id.toString();
    this.deletedEntityIds.add(deletedId);
    const keepEntity = <T extends IBizEntity>(items: T[]): T[] =>
      items.filter(item => item.id.toString() !== deletedId);

    this.strategyData = keepEntity(this.strategyData);
    this.strategyWithMeasureData = keepEntity(this.strategyWithMeasureData);
    this.measureData = keepEntity(this.measureData);
    this.explorerAttributes.data = keepEntity(this.explorerAttributes.data);
    this.explorerNavAttributes.data = keepEntity(this.explorerNavAttributes.data);
    this.explorerContentAttributes.data = keepEntity(this.explorerContentAttributes.data);
    this.explorerContentAttributes.listData.items = this.explorerContentAttributes.listData.items
      .filter(item => String(item.id) !== deletedId);

    const parentId = entity.parentId?.toString() ?? '';
    const bufferedItems = this.paginationBuffer.getItems(parentId);
    if (bufferedItems.some(item => item.id.toString() === deletedId)) {
      const totalCount = this.paginationBuffer.getTotalCount(parentId) ?? bufferedItems.length;
      this.paginationBuffer.setInitialPage(parentId, {
        items: keepEntity(bufferedItems),
        totalCount: Math.max(0, totalCount - 1),
      });
    }

    refreshExplorerNav(this.measureExplorer);
    this.measureExplorer?.interceptListActions(this.explorerContentAttributes.listData);
  }

  private isInMeasureStoreSubtree(entityId: string): boolean {
    const measureStore = this.strategyData.find(
      (entity) => entity.type.category === BizEntityTypeCategory.BenzaitenMeasureStore
    );
    let current = this.strategyData.find((entity) => entity.id === entityId);
    const visited = new Set<string>();
    while (current && !visited.has(current.id)) {
      if (current.id === measureStore?.id) return true;
      visited.add(current.id);
      current = this.strategyData.find((entity) => entity.id === current?.parentId);
    }
    return false;
  }

  private getExplorerCategories(): BizEntityTypeCategory[] {
    return Object.values(BizEntityTypeCategory).filter(
      (category): category is BizEntityTypeCategory =>
        typeof category === 'number' && !isExcludedFromApplicationExplorer(category)
    );
  }

  private defineExplorerContentAttributes(itemId: string | null = this.explorerAttributes.data.find((item) => item.parentId === '' || item.parentId === null)?.id || '', asyncItems?: IListViewItem<IBizEntity>[]) {

    // console.log('Defining explorer content attributes for itemId:', itemId);
    this.explorerContentAttributes.data = [...this.explorerAttributes.data];
    let item = this.explorerContentAttributes.data.find((item) => item.id === itemId);
    if (item && item.type.category === BizEntityTypeCategory.BenzaitenMeasure) {
      item = this.measureData.find((measure) => measure.id === itemId);
      // use asyncItems when available — sync prepareListData() would return stale/empty measureLogData
      this.explorerContentAttributes.listData = { title: '', colHeaders: getColHeadersForTxnLog(item as IMeasure), items: asyncItems ?? this.prepareListData(itemId!) };
      // Hide view option when displaying measure logs
      this.subHeaderAttributes.view = false;
      //Review following code, might need some changes. (request addressed to @mudit by @sanjay)
      if(!this.isInModal){
        this.setFloatingButtonAction(this.explorerContentAttributes.listData);
        }
      //---------------------------
    } else {
      // Show view option when displaying other entity types
      this.subHeaderAttributes.view = false;
      let children = this.extractItemChildren(itemId!);
      this.explorerContentAttributes.data = children; 

      const flags = {
        containsStructuralBizEntity: children.some(e => e.type.category === BizEntityTypeCategory.Structural || e.type.category === BizEntityTypeCategory.BenzaitenObjective || e.type.functionType === BizEntityFunctionTypes.Structural),
        containsDeliverableBizEntity: children.some(e => e.type.category === BizEntityTypeCategory.BenzaitenDeliverable),
        containsKPIBizEntity: children.some(e => e.type.category === BizEntityTypeCategory.BenzaitenKPI),
        containsMeasureBizEntity: children.some(e => e.type.category === BizEntityTypeCategory.BenzaitenMeasure),
      };
  
      let types:Set<BizEntityTypeCategory> = new Set();
      children.forEach(child => types.add(child.type.category));
  
      const colHeaders = getCombinedColHeadersForBizEntities(
        flags.containsStructuralBizEntity,
        flags.containsDeliverableBizEntity,
        flags.containsKPIBizEntity,
        flags.containsMeasureBizEntity
      );

      if(children.length > 0 && this.explorerContentAttributes.data.some((child) => child.type.category === BizEntityTypeCategory.BenzaitenMeasure)){
        this.explorerContentAttributes.listData = 
        { 
          title: '',
          colHeaders: colHeaders,
          items: this.prepareListData(itemId!, colHeaders),
          hasTableAutoInsertedRow:true
        };
      }else{
        this.explorerContentAttributes.listData = 
        {
          title: '',
          colHeaders: colHeaders,
          items: this.prepareListData(itemId!, colHeaders),
          hasTableAutoInsertedRow:true
         };
      }
    }

    


    // console.log('explorerContentAttributes',this.explorerContentAttributes);
    this.measureExplorer?.interceptListActions(this.explorerContentAttributes.listData);
  }

  private setFloatingButtonAction(listData: IListView<IBizEntity>) {
    const actionDisabled: boolean = this.parentSelectedForFloatingAddButton?.type.category !== BizEntityTypeCategory.BenzaitenMeasure;
    const floatingButtonLabel = 'Add Measure Log';
    const floatingButtonToolTip = actionDisabled ? 'Add Log' : 'Add Log'; //TODO: Add tooltip for disabled button, but it is not working, so changed.
    let addNew = true;
    if (!listData.hasOwnProperty('headerActions') || !listData.headerActions) {
      listData.headerActions = [];
    } else {
      listData.headerActions.map((action) => {
        if (action.label === floatingButtonLabel && action.isFloating) {
          action.isDisabled = actionDisabled;
          addNew = false;
        }
      });
    }
    if (addNew) {
      let action = { label: floatingButtonLabel, icon: 'cil-plus', action: this.onFloatButtonClicked, isFloating: true, tooltip: floatingButtonToolTip, isDisabled: actionDisabled };
      listData.headerActions.push(action);
    }
    return
  }

  private prepareListData(itemId: string, colHeaders?: IListViewColHeader[]): IListViewItem<IBizEntity>[] {
    let selectedEntityChildren = [];
    let item = this.explorerAttributes.data.find(entity => entity.id === itemId);
    this.parentSelectedForFloatingAddButton = item;
    let listItems: IListViewItem<IMeasure | ItxnRelationshipLog | IBizEntity>[] = [];
    let items: IListViewItem<any>[] = [];

    const children = this.extractItemChildren(itemId);
    const itemMap = new Map(listItems.map(i => [i.id, i]));

    if (item && item.type.category === BizEntityTypeCategory.BenzaitenMeasure) {
      selectedEntityChildren = this.measureLogData.filter((entity) => entity.bizEntity.id.toString() === itemId.toString());
      listItems = getListViewForMeasureLogs(selectedEntityChildren, this.measureLogItemclicked, this.selectClickedItem, this.isInModal, this.utilService, this.onMeasureLogDeleteClicked) as unknown as IListViewItem<IMeasure | ItxnRelationshipLog | IBizEntity>[];
      return listItems as IListViewItem<IBizEntity>[];
    }
    else {
      selectedEntityChildren = this.extractItemChildren(itemId);
      if(selectedEntityChildren.length > 0){
        const childTagDataPromise = this.getTagData(selectedEntityChildren);
        listItems = getListViewItemsForBizEntities(
          selectedEntityChildren,
          (item: IListViewItem<IBizEntity> | undefined, event?: Event) => this.menuButtonClicked(item as IListViewItem<IMeasure> | undefined, event),
          this.itemClicked,
          this.selectClickedItem,
          this.isInModal,
          childTagDataPromise,
          this.linkedEntityPickerService
        ) as unknown as IListViewItem<IMeasure | ItxnRelationshipLog | IBizEntity>[];
        listItems.forEach(di => itemMap.set(di.id, di));

        if(selectedEntityChildren.some((child) => child.type.category === BizEntityTypeCategory.BenzaitenMeasure)){
          selectedEntityChildren = this.measureData.filter((entity) => entity.parentId === itemId);
          const measureTagDataPromise = this.getTagData(selectedEntityChildren);
          listItems = getListViewForMeasures(
            selectedEntityChildren,
            this.menuButtonClicked,
            this.itemClicked,
            this.onAddButtonClicked,
            this.selectClickedItem,
            this.isInModal,
            measureTagDataPromise,
            this.linkedEntityPickerService
          ) as unknown as IListViewItem<IMeasure | ItxnRelationshipLog | IBizEntity>[];
          listItems.forEach(di => itemMap.set(di.id, di));
        }
      } else{
        const measureTagDataPromise = this.getTagData(selectedEntityChildren);
        listItems = getListViewForMeasures(
          selectedEntityChildren,
          this.menuButtonClicked,
          this.itemClicked,
          this.onAddButtonClicked,
          this.selectClickedItem,
          this.isInModal,
          measureTagDataPromise,
          this.linkedEntityPickerService
        ) as unknown as IListViewItem<IMeasure | ItxnRelationshipLog | IBizEntity>[];
        listItems.forEach(di => itemMap.set(di.id, di));
      }
      
    }

    // return listItems as IListViewItem<IBizEntity>[];
    items = Array.from(itemMap.values());

    return colHeaders ? normalizeListItems(items, colHeaders) : listItems as IListViewItem<any>[];
  }

  private prepareListDataAsync(itemId: string): Promise<IListViewItem<IBizEntity>[]> {
    const item = this.explorerAttributes.data.find(entity => entity.id === itemId);
    this.parentSelectedForFloatingAddButton = item;

    // If item is a measure, load its logs asynchronously
    if (item?.type.category === BizEntityTypeCategory.BenzaitenMeasure) {
      return Promise.resolve(this.measureLogData)
        .then((logs) => {
          // Enrich logs with measure data
          logs.forEach((log) => {
            const measure = this.measureData.find((m) => m.id.toString() === log.bizEntity?.id.toString());
            if (measure) {
              log.bizEntity = measure;
            }
          });
          
          return getListViewForMeasureLogs(
            logs,
            this.measureLogItemclicked,
            this.selectClickedItem,
            this.isInModal,
            this.utilService,
            this.onMeasureLogDeleteClicked
          ) as unknown as IListViewItem<IBizEntity>[];
        });
    }

    // For non-measure items, return synchronously wrapped in Promise
    const syncListItems = this.prepareListData(itemId);
    return Promise.resolve(syncListItems);
  }


  private menuButtonClicked = (item: IListViewItem<IMeasure> | undefined, event?: Event) => { }
  _selectedMeasure: IMeasure | undefined;

  selectClickedItem = (itemId: string) => {
    let item:IBizEntity | undefined = this.measureData.find(user => user.id === itemId)!;
    if(!item){
      item = this.strategyWithMeasureData.find(user => user.id === itemId)!;
    }
    this.parentSelectedForFloatingAddButton = item;
    if (this.isInModal) {
      this._selectedMeasure = item as IMeasure;
      this.onSelectionChange?.(this._selectedMeasure);
    }
  }

  get data(): IMeasure | undefined {
    return this._selectedMeasure;
  }

  navItemClicked = (itemId: string) => {
    this.isLoading.set(true);
    this.explorerNavAttributes.isLoading = false;
    
    let item:IBizEntity | undefined = this.measureData.find(user => user.id === itemId)!;
    if(!item){
      item = this.strategyWithMeasureData.find(user => user.id === itemId)!;
    }
    this.parentSelectedForFloatingAddButton = item;
    if (this.isInModal) {
      this._selectedMeasure = item as IMeasure;
      this.onSelectionChange?.(this._selectedMeasure);
    }
    
    this.updateListData(itemId)
      .then(() => {
        this.isLoading.set(false);
        this.explorerNavAttributes.isLoading = false;
      })
      .catch((error) => {
        console.error('Error updating list data:', error);
        this.isLoading.set(false);
        this.explorerNavAttributes.isLoading = false;
      });
    this.resetIncomingBizEntitySelectionId();
  }

  itemClicked = (itemId: string, actionOnOperationalElementClicked: boolean = true) => {
    this.subHeaderAttributes.previouseDisable = false;
    let item: any;
    item = this.explorerAttributes.data.find(entity => entity.id === itemId)
    if (!item) {
      // item = this.listOfUsers.find(user => user.id === itemId)! as IBizEntity;
      item = this.measureLogData.find(measure => measure.id.toString() === itemId.toString())!;
      if(item){
        // Log rows aren't biz entities; route them to the measure-log edit handler instead.
        this.measureLogItemclicked(itemId);
        this.resetIncomingBizEntitySelectionId();
        return;
      }
    }

    // let itemHasChildren = this.explorerAttributes.data.some(entity => entity.parentId === itemId);
    // let teamMembers = item?.type.category === BizEntityTypeCategory.Team ? this.extractTeamMembers(itemId) : [];
    if (item?.type) {
      this.isLoading.set(true);
      
      this.updateListData(itemId)
        .then(() => {
          this.isLoading.set(false);
        })
        .catch((error) => {
          console.error('Error updating list data:', error);
          this.isLoading.set(false);
        });
    }
    else {
      if (!this.isInModal) {
        if(item.type.functionType === BizEntityFunctionTypes.Operational){
          this.onActionButtonClicked(itemId);
        }
      }
    }
    this.resetIncomingBizEntitySelectionId();
  }

  measureLogItemclicked = (itemId: string) => {
    this.appStateService.entityFormState = FormStates.Edit;
    this.appStateService.currentSelectedTxnLogItem = this.measureLogData.find(measure => measure.id.toString() === itemId.toString())!;
    const level = this.offCanvasFormService.currentOffCanvas?.level ?? 0;
    // compIsABizEntity must be true: AppStateService resets entityFormState to Idle as soon as the
    // off-canvas stack has no entry flagged as a biz entity, which otherwise raced the form's ngOnInit
    // (reading entityFormState back as Idle) and caused the edit form to render blank instead of patching values.
    openOffCanvas(this.offCanvasFormService, this.offCanvasServiceRef, OffCanvasComponents.TxnLog, getOffCanvasIdFromLevel(level + 1), level + 1, true);
  }

  private onMeasureLogDeleteClicked = (itemId: string) => {
    this.measureLogStateStoreService.delete(itemId)!.then((response)=>{
      if (response) {
        this.toastService.toast('Measure log deleted successfully.');
      } else {
        this.toastService.toast('Failed to delete measure log.');
      }
    }).catch((error) => {
      this.toastService.toast('Error deleting measure log.');
    })
  };
  
  /** Thorough review required */
  private parentSelectedForFloatingAddButton: IBizEntity | undefined;
  /** Thorough review required */
  onFloatButtonClicked = (item: IListViewItem<IBizEntity> | undefined, event?: Event | undefined) => {
    //considering Lister is directly calling it (at so deep level there is no selected item passed by the client)
    //we need to have an already selected entity in this client
    let measure = this.measureData.find(measure => measure.id === this.parentSelectedForFloatingAddButton?.id);

    if (!measure) {
      this.toastService.toast('No measure is selected to add log to!');
      return;
    }

    // this.appStateService.selectedMeasure.measure =

    // Following code is same as onAddButtonClicked. In both the cases setting entityFormState is done wrongly.
    // CAUTION : This is a bug. Need to be fixed at both the places.
    if (measure) {
      this.appStateService.setSelectedMeasure(measure);
      // this.appStateService.setMeasureLog(mLog);
    }
    // Preserve the parent measure selection so explorer reselects it after log creation
    this.appStateService.incomingBizEntitySelectionId = measure.id;
    this.appStateService.entityFormState = FormStates.New;
    const level = this.offCanvasFormService.currentOffCanvas?.level ?? 0;
    openOffCanvas(this.offCanvasFormService, this.offCanvasServiceRef, OffCanvasComponents.TxnLog, getOffCanvasIdFromLevel(level + 1), level + 1);
    this.appStateService.entityFormState = FormStates.Idle;

  }

  onAddButtonClicked = (item: IListViewItem<IMeasure> | undefined, event?: Event | undefined) => {

    let measure = this.measureData.find(measure => measure.id === item?.id);
    // this.appStateService.selectedMeasure.measure =

    if (measure) {
      this.appStateService.setSelectedMeasure(measure);
      // this.appStateService.setMeasureLog(mLog);
    }
    // Preserve the parent measure selection so explorer reselects it after log creation
    this.appStateService.incomingBizEntitySelectionId = measure?.id || '';
    this.appStateService.entityFormState = FormStates.New;
    const level = this.offCanvasFormService.currentOffCanvas?.level ?? 0;
    openOffCanvas(this.offCanvasFormService, this.offCanvasServiceRef, OffCanvasComponents.TxnLog, getOffCanvasIdFromLevel(level + 1), level + 1);
    this.appStateService.entityFormState = FormStates.Idle;
  }

  processContentContextMenuClicked(event: ContextMenuClickInfo): void {
    const { id: itemId, event: domEvent } = event;
    if (!itemId) {
      // console.error('Invalid item ID.');
      return;
    }

    // let bizEntityItem = this.benBizEntityMockDataService.getItem(itemId!, getTenantIdInString(this.tenantService.organization.tenantId), this.mockAPIResponseService);
    let item = this.explorerAttributes.data.find(entity => entity.id === itemId);

    this.appStateService.selectedBizEntityForEdit = item! as IMeasure;
    this.appStateService.selectedBizEntityTypeForNewBizEntity = this.appStateService.selectedBizEntityForEdit.type;
    // this.appStateService.setSelectedMeasure(this.measureData.find(measure => measure.id === itemId)!);
    if (item && item.type.category === BizEntityTypeCategory.BenzaitenMeasure) {
      item = this.measureData.find(measure => measure.id === itemId);
      if (item) {
        this.appStateService.setSelectedMeasure(item as IMeasure);
        // this.appStateService.setMeasureLog(mLog);
      }
    }
    this.contextMenuService.bizEnitityEvent.emit(item);
    if (item && item.type) {
      this.updateContextMenuList(item, [...this.contextMenuItems]);
    }
  }

  updateContextMenuList(entity:IBizEntity, menuItems: ContextMenuItem[])
  {
    const list = this.contextMenuService.getEntityListBasedOnType(entity, menuItems);
    this.explorerAttributes.contextMenuItems = list;

    // When the "Cross App" option is present, fetch the related entities for each
    // cross-app relationship target type asynchronously and reassign a NEW array
    // reference so an already-open context menu re-renders with the populated
    // sub-items.
    this.contextMenuService.populateCrossAppSubItems(entity, list).then(updatedList => {
      if (updatedList) {
        this.explorerAttributes.contextMenuItems = updatedList;
      }
    });
  }


  private extractItemChildren(itemId: string): IMeasure[] {
    let item = this.utilService.extractItemChildren(itemId, this.strategyWithMeasureData);
    return item as IMeasure[];
  }

  onActionButtonClicked(itemId: string) {
    //CAUTION: Following is temporary arrangement to set selected biz entity for edit because plan element is a not a biz entity type node.
    this.appStateService.entityFormState = FormStates.Edit;
    // this.appStateService.selectedBizEntityForEdit = this.benBizEntityMockDataService.getEmptyForPlanElement(this.offCanvasFormService.planElementForm, this.tenantService.currentUser);
    // const item = this.benBizEntityMockDataService.getItem(itemId, getTenantIdInString(this.tenantService.organization.tenantId), this.mockAPIResponseService);
    let item = this.strategyWithMeasureData.find(entity => entity.id === itemId);
    if (!item) {
      // item = this.measureLogData.find(user => user.id === itemId)!;
    }
    this.appStateService.selectedBizEntityForEdit = item! as IBenBizEntity;
    this.appStateService.selectedBizEntityTypeForNewBizEntity = this.appStateService.selectedBizEntityForEdit.type;
    this.openBizEntityFormInOffCanvas();
  }

  private openBizEntityFormInOffCanvas = () => {
    let comp: OffCanvasComponents | null = null;
    let shouldOpen = false;
    if (this.appStateService.entityFormState == FormStates.New) {
      // console.log("in constructor > For new, Selected biz entity type - " + this.appStateService.selectedBizEntityTypeForNewBizEntity?.name)
      const t = this.appStateService.selectedBizEntityTypeForNewBizEntity;
      comp = getOffCanvasComponentForBizEntityCategory(t ? t : undefined);
      if (comp !== null) {
        // console.log(`[NEW] For BizEntity type ${t?.name}, component selected is - ` + getKeyOfEnumValue(comp, OffCanvasComponents));
        shouldOpen = true;
      } else {
        console.error("[NEW] No off-canvas component found for the selected biz entity type.");
      }
    } else if (this.appStateService.entityFormState == FormStates.Edit && this.appStateService.selectedBizEntityForEdit !== null) {
      const t = this.appStateService.selectedBizEntityForEdit.type;
      comp = getOffCanvasComponentForBizEntityCategory(t ? t : undefined);
      if (comp !== null) {
        // console.log(`[EDIT] For BizEntity type ${t?.name}, component selected is - ` + getKeyOfEnumValue(comp, OffCanvasComponents));
        shouldOpen = true;
      } else {
        console.error(`[EDIT] No off-canvas component found for ${t?.name}`);
      }
    }
    if (shouldOpen && comp !== null) {
      const level = this.offCanvasFormService.currentOffCanvas?.level ?? 0;
      openOffCanvas(this.offCanvasFormService, this.offCanvasServiceRef, comp, getOffCanvasIdFromLevel(level + 1), level + 1, true); //last param is important
      // console.log('Edit/Views',FormStates[this.appStateService.entityFormState]);
    } else {
      this.appStateService.entityFormState = FormStates.Idle;
    }
  }

  private openOthersInRightOffCanvas = () => {
    if (this.appStateService.offCanvasRightContentType === OffCanvasRightContentTypes.Others) {
      const level = this.offCanvasFormService.currentOffCanvas?.level ?? 0;
      openOffCanvas(this.offCanvasFormService, this.offCanvasServiceRef, this.appStateService.offCanvasRightComponent, getOffCanvasIdFromLevel(level + 1), level + 1, false);
    }
  }

  private openDetailOffCanvas = () => {
    if(this.appStateService.offCanvasRightContentType === OffCanvasRightContentTypes.DetailForm){
      let entity = this.strategyWithMeasureData.find(entity => entity.id === this.appStateService.selectedBizEntityForEdit?.id);
      if(!entity){
        // Selected entity not in current data set → abort cleanly, otherwise entityFormState stays stuck.
        this.appStateService.entityFormState = FormStates.Idle;
        return;
      }
      this.appStateService.offCanvasDetailActiveFor = entity;
      this.entityBottomOffCanvasService.open(entity);
      // this.offCanvasServiceRef.toggle({
      //   id: 'bottom-off-canvas-in-default-layout',
      //   show: true,
      // });
    }
  }

  addToQuickActionClicked() {
    // console.log('Measure add to quick action called', this.appStateService.selectedBizEntityForEdit);
  }


  resetIncomingBizEntitySelectionId() {
    this.appStateService.incomingBizEntitySelectionId = null;
  }

  private getTagData(entities: IBizEntity[]) {
    const entityIds = entities.map((entity) => entity.id);
    return Promise.all([
      this.appTagService.getAvailableTags(),
      this.appTagService.getSystemTagBizEntityType(),
      this.appTagService.getAssignedTags(entityIds),
    ]).then(([availableTagsFromAPI, tagBizEntityTypeFromAPI, parentAssignedTagsFromAPI]) => ({
      availableTagsFromAPI,
      tagBizEntityTypeFromAPI,
      parentAssignedTagsFromAPI,
    }));
  }
}
